import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, LogIn, AlertCircle } from 'lucide-react';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    // Trim email and password to prevent whitespace issues from autofill/password managers
    const cleanEmail = email.trim().toLowerCase();
    const cleanPassword = password.trim();
    const result = await login(cleanEmail, cleanPassword);
    
    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }
    
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1749704647804-81338ade5546?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBpbnRlcmlvciUyMGRlc2lnbiUyMHJlbm92YXRpb258ZW58MHx8fHwxNzc0NTAxNjA5fDA&ixlib=rb-4.1.0&q=85"
          alt="Modern interior renovation"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent" />
        <div className="absolute bottom-12 left-12 text-white max-w-md">
          <h2 className="text-3xl font-light mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Gestisci i tuoi progetti di ristrutturazione con semplicità
          </h2>
          <p className="text-white/80 text-lg">
            ABITALO CRM - La piattaforma completa per la gestione di cantieri e clienti
          </p>
        </div>
      </div>
      
      {/* Right side - Login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#FAFAFA]">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center mb-4">
              <img 
                src="/images/abitalo-logo.png" 
                alt="Abitalo" 
                className="h-12 object-contain"
              />
            </div>
            <p className="text-[#737373]">Accedi al tuo account</p>
          </div>
          
          {/* Login Form */}
          <div className="card-abitalo p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="flex items-center gap-2 p-4 bg-[#FFEDED] border border-[#FFB3B3] rounded-lg text-[#CC0000]">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#3C3C3C]">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="nome@abitalo.it"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-abitalo"
                  required
                  data-testid="login-email-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#3C3C3C]">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-abitalo pr-10"
                    required
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#737373] hover:text-[#3C3C3C] transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              
              {/* Forgot Password Link */}
              <div className="text-right">
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-[#FF7819] hover:text-[#e66a0f] transition-colors"
                >
                  Password dimenticata?
                </Link>
              </div>
              
              <Button
                type="submit"
                className="w-full btn-primary h-12"
                disabled={isLoading}
                data-testid="login-submit-btn"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Accesso in corso...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <LogIn className="w-5 h-5" />
                    Accedi
                  </span>
                )}
              </Button>
            </form>
          </div>
          
          {/* Footer */}
          <p className="text-center mt-8 text-sm text-[#737373]">
            © {new Date().getFullYear()} ABITALO. Tutti i diritti riservati.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
